import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ArrowUpRight, ShieldCheck } from "lucide-react";
import Logo from "@/components/Logo";
import { adminLogin } from "@/lib/adminAuth";

export default function AdminLogin() {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    setError("");
    try {
      await adminLogin(password);
      navigate("/admin");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not log in.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] text-stone-900 flex items-center" data-testid="admin-login">
      <div className="mx-auto max-w-md px-6 w-full">
        <div className="mb-10"><Logo /></div>

        <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase font-bold text-[#9C4221]">
          <ShieldCheck className="h-3 w-3" strokeWidth={2.5} /> Admin
        </div>
        <h1 className="mt-4 font-display text-3xl md:text-4xl tracking-tight font-bold text-stone-900 leading-tight">
          Founder access.
        </h1>
        <p className="mt-3 text-stone-700 leading-relaxed">
          One password. 24-hour session. View contact enquiries, readiness completions and download counts.
        </p>

        <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="admin-login-form">
          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase font-bold text-stone-500 mb-2" htmlFor="admin-password">
              Password
            </label>
            <div className="relative">
              <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <input
                id="admin-password"
                type="password"
                autoComplete="current-password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password-input"
                className="w-full border border-stone-300 bg-white pl-10 pr-3 py-3 text-sm rounded-sm focus:outline-none focus:border-stone-900 transition-colors"
                placeholder="Enter admin password"
              />
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2" data-testid="admin-login-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !password}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-stone-900 text-white px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-stone-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? "Signing in…" : "Sign in"}
            <ArrowUpRight className="h-4 w-4" />
          </button>

          <p className="text-[11px] text-stone-500 italic leading-relaxed pt-2">
            Five wrong attempts trigger a five-minute lockout. Sessions expire after 24 hours.
          </p>
        </form>
      </div>
    </div>
  );
}
